import React from "react";
import {
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";

import {
  getExternalRegisterAlertPresentation,
} from "./helpers";
import { useTranslation } from "react-i18next";
import type {
  ExternalRegisterAuditEvent,
  ExternalRegisterDetails as ExternalRegisterDetailsRecord,
} from "./types";

type ExternalRegisterDetailsProps = {
  alerts: ExternalRegisterDetailsRecord[];
};

type DetailField = {
  label: string;
  value?: string;
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

const formatAuditAction = (action: string): string => {
  return action.replace(/_/g, " ").replace(/^\w/, (letter) => letter.toUpperCase());
};

const formatOptionalValue = (value: string | null | undefined, t: Translate): string => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : t("common.empty.notRecorded");
};

const formatDateTime = (value: string | null | undefined, t: Translate): string => {
  if (!value) return t("common.empty.notRecorded");

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  if (isDateOnly) {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }).format(date);
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  }).format(date);
};

const sortAuditHistoryForDisplay = (
  history: ExternalRegisterAuditEvent[],
): ExternalRegisterAuditEvent[] => {
  return [...history].sort((a, b) => {
    const aTime = Date.parse(a.timestamp);
    const bTime = Date.parse(b.timestamp);
    if (Number.isNaN(aTime) || Number.isNaN(bTime)) return 0;
    return bTime - aTime;
  });
};

const DetailsGrid = ({ fields }: { fields: DetailField[] }) => {
  const { t } = useTranslation();

  return (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(2, minmax(0, 1fr))",
      },
      gap: 1,
    }}
  >
    {fields.map((field) => (
      <Box
        key={field.label}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          px: 1,
          py: 0.65,
          minWidth: 0,
          bgcolor: "background.default",
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 700 }}>
          {field.label}
        </Typography>
        <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
          {formatOptionalValue(field.value, t)}
        </Typography>
      </Box>
    ))}
  </Box>
  );
};

const CommentsPanel = ({ comments }: { comments?: string }) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: comments?.trim() ? "primary.light" : "divider",
        bgcolor: comments?.trim() ? "primary.25" : "background.default",
        borderRadius: 1,
        p: 1,
      }}
    >
      <Typography variant="caption" sx={{ display: "block", fontWeight: 800, mb: 0.35 }}>
        {t("externalRegister.details.sections.comments")}
      </Typography>
      <Typography
        variant="body2"
        color={comments?.trim() ? "text.primary" : "text.secondary"}
        sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
      >
        {comments?.trim() ? comments : t("externalRegister.details.empty.noComments")}
      </Typography>
    </Box>
  );
};

const AuditHistoryPanel = ({ history }: { history: ExternalRegisterAuditEvent[] }) => {
  const { t } = useTranslation();
  const sortedHistory = sortAuditHistoryForDisplay(history);

  return (
    <Box>
      <Typography variant="caption" sx={{ display: "block", fontWeight: 800, mb: 0.5 }}>
        {t("externalRegister.details.sections.auditHistory")}
      </Typography>
      {sortedHistory.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("externalRegister.details.empty.noHistory")}
        </Typography>
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ borderRadius: 1, boxShadow: "none" }}
        >
          <Table size="small" aria-label={t("externalRegister.details.table.ariaLabel")}>
            <TableBody>
              {sortedHistory.map((event, eventIndex) => (
                <TableRow key={`${event.timestamp}-${event.action}-${eventIndex}`}>
                  <TableCell sx={{ width: 135, fontWeight: 700, py: 0.55 }}>
                    {formatAuditAction(event.action)}
                  </TableCell>
                  <TableCell sx={{ width: 170, py: 0.55 }}>
                    {formatDateTime(event.timestamp, t)}
                  </TableCell>
                  <TableCell sx={{ py: 0.55 }}>
                    <Typography variant="body2">
                      {formatOptionalValue(event.userName, t)}
                    </Typography>
                    {event.actingAsClinician ? (
                      <Typography variant="caption" color="text.secondary">
                        {t("externalRegister.details.audit.actingAs", { clinician: event.actingAsClinician })}
                      </Typography>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export const ExternalRegisterDetails: React.FC<ExternalRegisterDetailsProps> = ({ alerts }) => {
  const { t } = useTranslation();

  if (alerts.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("externalRegister.details.empty.noSecureDetails")}
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {alerts.map((alert) => {
        const presentation = getExternalRegisterAlertPresentation(alert);
        const aliasesText = alert.aliases?.join(", ") ?? "";

        return (
          <Paper
            key={alert.id}
            variant="outlined"
            sx={{
              borderRadius: 1,
              overflow: "hidden",
              borderLeft: "4px solid",
              borderLeftColor: presentation.color,
            }}
          >
            <Box sx={{ px: 1.25, py: 1, bgcolor: "primary.25" }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  label={presentation.label}
                  sx={{
                    borderRadius: 1,
                    backgroundColor: presentation.color,
                    color: (theme) => theme.palette.getContrastText(presentation.color),
                    fontWeight: 700,
                  }}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={formatAuditAction(alert.status)}
                  sx={{ borderRadius: 1, fontWeight: 700 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {[
                    t("externalRegister.details.header.listed", { date: formatDateTime(alert.dateListed, t) }),
                    formatOptionalValue(alert.sourceHospital, t),
                  ].filter(Boolean).join(t("externalRegister.details.header.separator"))}
                </Typography>
              </Stack>
            </Box>

            <Divider />

            <Box sx={{ p: 1.25 }}>
              <Stack spacing={1}>
                <DetailsGrid
                  fields={[
                    { label: t("externalRegister.details.fields.alertType"), value: presentation.label },
                    { label: t("externalRegister.details.fields.sourceHospital"), value: alert.sourceHospital },
                    { label: t("externalRegister.details.fields.dateListed"), value: formatDateTime(alert.dateListed, t) },
                    { label: t("externalRegister.details.fields.status"), value: formatAuditAction(alert.status) },
                    { label: t("externalRegister.details.fields.aliases"), value: aliasesText },
                  ]}
                />
                <CommentsPanel comments={alert.comments} />
                <AuditHistoryPanel history={alert.history} />
              </Stack>
            </Box>
          </Paper>
        );
      })}
    </Stack>
  );
};

export default ExternalRegisterDetails;
